import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { forkJoin, Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { FileType, UploadProgress, UploadResponse } from '../../models';
import { DialogService } from '../../services/dialog.service';
import { UploadEndpoint, UploadService } from '../../services/upload.service';


@Component({
  selector: 'app-upload-field',
  templateUrl: './upload-field.component.html',
  styleUrls: ['./upload-field.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UploadFieldComponent implements OnInit {
  uploads: UploadFile[];
  filesToValidate: number;
  capture: string;
  accept: string; 
  minSizeError: boolean;
  @ViewChild('uploadSheet', { static: true })uploadSheet: TemplateRef<any>;
  @ViewChild('fileInput', { static: true })fileInput: ElementRef;
  @Input()
  endpoint: UploadEndpoint;
  @Input()
  hubId?: number;
  @Input()
  businessId?: number;  
  @Input()
  folderId?: number;
  @Input()
  fileType: FileType = FileType.Temporary;
  @Input()
  multiple: boolean = false;
  @Input()
  closeOnComplete: boolean = false;
  @Input()
  minWidth?: number;
  @Input()
  channelId?: number;
  @Output('onUpload')
  onUpload = new EventEmitter<UploadResponse>();
  @Output('onComplete')
  onComplete = new EventEmitter();

  constructor(private uploadService: UploadService, private bottomSheet: MatBottomSheet, private dialogs: DialogService) { }

  ngOnInit() {
    if (this.endpoint === UploadEndpoint.BusinessImage || this.endpoint === UploadEndpoint.UserProfileImage) {
      this.accept = "image/*";
    }
    else if (this.endpoint === UploadEndpoint.BusinessFile) {
      this.accept = ".pdf,.mp3";
    }
  }

  @HostListener('click')
  onClick() {
    this.fileInput.nativeElement.click();
  }

  handleFiles(event: any) {
    this.uploads = [];
    this.filesToValidate = 0;
    this.minSizeError = false;
    let files = event.target.files as FileList;
    if (files.length === 0)
      return;

    for (var i = 0; i < files.length; i++) {
      this.uploads.push({ file: files.item(i), status: null });
    }

    if(this.endpoint !== UploadEndpoint.BusinessImage || !this.minWidth) {
      // normal upload
      this.startUpload();
    } else {
      // validate image size before upload
      const observables = [];
      for (let i = 0; i < files.length; i++) {
        observables.push(this.validateImage(files[i]));
      }

      forkJoin(observables)
      .subscribe(result => {
        if (result.some(x => !x)) {
          this.validationError($localize`Bilden är för liten. Den måste vara minst ` + this.minWidth + $localize`px bred.`);
          this.uploads = [];
        }
        else {
          this.startUpload();
        }
      });
    }
  }

  startUpload() {
    this.bottomSheet.open(this.uploadSheet, { closeOnNavigation: false, disableClose: true }).afterOpened().subscribe(() => {
      const observables = this.uploads.map(item => {
          return this.uploadService.upload(item.file, this.fileType, this.endpoint, this.hubId, this.businessId, this.folderId, this.channelId).pipe(
            filter(x => !!x),
            tap(response => {
              var item = this.uploads.find(x => x.file.name === response.fileName);
              if (item)
                item.status = response;
            }),
            filter(r => r.status === 'complete'),
            tap(response => {
              if (!response.body.error) {
                this.onUpload.next(response.body);
              }
              else {
                throw { error: response.body.error };
              }
            })
          )
      });

      forkJoin(observables).subscribe(uploads => {
        if (this.closeOnComplete) {
          this.bottomSheet.dismiss();
        }
        this.onComplete.emit();
      });
    });
  }

  cancelUpload(file: UploadFile) {
    var idx = this.uploads.findIndex(x => x.file.name === file.file.name);
    this.uploads.splice(idx, 1);
    if (this.uploads.length === 0) {
      this.bottomSheet.dismiss();
    }
  }

  validationError(message: string) {
    this.dialogs.message(message, $localize`Ok`);
  }

  private validateImage(fileItem: File): Observable<boolean> {
    return new Observable<boolean>(subject => {
      let valid = true;
      const reader = new FileReader();
      reader.onload = (e: any) => {
          const image = new Image();
          image.src = e.target.result;
          image.onload = rs => {
            const img_height = rs.currentTarget['height'];
            const img_width = rs.currentTarget['width'];            
            if(this.minWidth && img_height < this.minWidth && img_width < this.minWidth)
            {
              valid = false;
            }

            subject.next(valid);
            subject.complete();
          };
      };

      reader.onerror = () => {
        subject.next(false);
        subject.complete();
      }

      reader.readAsDataURL(fileItem);
    });
  }
}

class UploadFile {
  file: File;
  status: UploadProgress;
}

@Component({
  selector: 'app-upload-item',
  templateUrl: './upload-item.component.html'
})
export class UploadItem implements AfterViewInit {
  @Input()
  file: UploadFile;
  @Output()
  onCancel = new EventEmitter<UploadFile>();
  private imageTypes = ['image/jpeg','image/png'];
  imageUrl: SafeResourceUrl;
  fileSize: string;

  constructor(private sanitizer: DomSanitizer) {}

  ngAfterViewInit() {
    if (this.isImage(this.file.file)) {
      this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(this.file.file));
    }

    if(this.file.file.size < 1024) {
      this.fileSize = this.file.file.size + 'b';
    } else if(this.file.file.size >= 1024 && this.file.file.size < 1048576) {
      this.fileSize = (this.file.file.size/1024).toFixed(1) + 'KB';
    } else {
      this.fileSize = (this.file.file.size/1048576).toFixed(1) + 'MB';
    }    
  }

  cancel() {
    this.onCancel.emit(this.file);
  }

  private isImage(file: File) {
    return this.imageTypes.some(x => x === file.type);
  }
}